// src/pages/Profile/components/SettingsTab.js - UPDATED WITH NAME FIELDS AND BANK VALIDATION
import React, { useState, useEffect } from 'react';
import { useProfile } from '../../../hooks/useProfile';
import { SecurityValidator } from '../../../services/authService';

const SettingsTab = () => {
  // Profile state
  const [surname, setSurname] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Password strength indicator
  const [passwordStrength, setPasswordStrength] = useState({
    hasLength: false,
    hasLetter: false,
    hasNumber: false,
    hasSpecial: false
  });
  
  // Bank account state - SINGLE account only
  const [bankAccount, setBankAccount] = useState({
    id: null,
    bank_name: '',
    account_name: '',
    account_number: '',
    is_default: true
  });
  
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  
  const { 
    userProfile,
    bankAccounts,
    fetchUserProfile,
    updateProfile,
    changePassword,
    fetchBankAccounts,
    addBankAccount,
    updateBankAccount,
    deleteBankAccount,
    loading 
  } = useProfile();

  // Load profile data on mount
  useEffect(() => {
    loadProfileData();
  }, []);

  // Update password strength indicator
  useEffect(() => {
    if (newPassword) {
      setPasswordStrength({
        hasLength: newPassword.length >= 8,
        hasLetter: /[a-zA-Z]/.test(newPassword),
        hasNumber: /\d/.test(newPassword),
        hasSpecial: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(newPassword)
      });
    }
  }, [newPassword]);

  const loadProfileData = async () => {
    try {
      const profile = await fetchUserProfile();
      setSurname(profile.surname || '');
      setFirstName(profile.first_name || '');
      setMiddleName(profile.middle_name || '');
      setPhoneNumber(profile.phone_number || '');
      setEmail(profile.email || '');
      
      // ✅ FIX: Use the returned value from fetchBankAccounts directly.
      // Reading the `bankAccounts` hook state here would give us the stale
      // value from before the fetch because React state updates are async.
      const accounts = await fetchBankAccounts();
      
      if (accounts && accounts.length > 0) {
        const account = accounts[0]; // Only one account allowed
        setBankAccount({
          id: account.id,
          bank_name: account.bank_name || '',
          account_name: account.account_name || '',
          account_number: account.account_number || '',
          is_default: true
        });
        setIsEditingBank(false);
      } else {
        // No bank account yet — open form ready for input
        setIsEditingBank(true);
      }
    } catch (err) {
      showMessage('Failed to load profile data', 'error');
    }
  };

  const showMessage = (msg, type = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    if (!surname.trim() || !firstName.trim() || !middleName.trim() || !phoneNumber.trim()) {
      showMessage('Please fill in all profile fields', 'error');
      return;
    }

    // Validate name fields
    try {
      SecurityValidator.validateNameField(surname, 'Surname');
      SecurityValidator.validateNameField(firstName, 'First name');
      SecurityValidator.validateNameField(middleName, 'Middle name');
    } catch (err) {
      showMessage(err.message, 'error');
      return;
    }

    try {
      const updateData = {
        surname: surname.trim(),
        first_name: firstName.trim(),
        middle_name: middleName.trim(),
        phone_number: phoneNumber.trim()
      };
      
      await updateProfile(updateData);
      showMessage('Profile updated successfully!', 'success');
      
      // Reload profile to get updated data
      await loadProfileData();
    } catch (err) {
      showMessage(err.message || 'Failed to update profile', 'error');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      showMessage('Please fill in all password fields', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showMessage('New passwords do not match', 'error');
      return;
    }

    // Validate new password strength
    try {
      SecurityValidator.validatePassword(newPassword);
    } catch (err) {
      showMessage(err.message, 'error');
      return;
    }

    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      showMessage('Password changed successfully!', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      showMessage(err.message || 'Failed to change password', 'error');
    }
  };

  const validateBankAccountName = (accountName) => {
    // Get expected name format: "Surname Firstname Middlename"
    const expectedName = `${surname} ${firstName} ${middleName}`;
    
    // Normalize both names for comparison (case-insensitive)
    const expectedNormalized = expectedName.toLowerCase().replace(/\s+/g, ' ').trim();
    const providedNormalized = accountName.toLowerCase().replace(/\s+/g, ' ').trim();
    
    if (expectedNormalized !== providedNormalized) {
      throw new Error(
        `Account name must match your registered name: "${expectedName}". ` +
        `Please ensure the account name is exactly "${expectedName}" (case-insensitive).`
      );
    }
    
    return true;
  };

  const handleBankAccountSave = async (e) => {
    e.preventDefault();

    const { bank_name, account_name, account_number } = bankAccount;

    if (!bank_name.trim() || !account_name.trim() || !account_number.trim()) {
      showMessage('Please fill in all bank account fields', 'error');
      return;
    }

    if (account_number.length < 10) {
      showMessage('Account number must be at least 10 digits', 'error');
      return;
    }

    // Validate account name matches user's registered name
    try {
      validateBankAccountName(account_name);
    } catch (err) {
      showMessage(err.message, 'error');
      return;
    }

    try {
      if (bankAccount.id) {
        // Update existing account
        await updateBankAccount(bankAccount.id, {
          bank_name: bank_name.trim(),
          account_name: account_name.trim(),
          account_number: account_number.trim()
        });
        showMessage('Bank account updated successfully!', 'success');
      } else {
        // Add new account (first time)
        await addBankAccount({
          bank_name: bank_name.trim(),
          account_name: account_name.trim(),
          account_number: account_number.trim(),
          set_as_default: true
        });
        showMessage('Bank account added successfully!', 'success');
      }
      
      // ✅ FIX: Use returned value directly instead of reading stale hook state
      const accounts = await fetchBankAccounts();
      if (accounts && accounts.length > 0) {
        const account = accounts[0];
        setBankAccount({
          id: account.id,
          bank_name: account.bank_name || '',
          account_name: account.account_name || '',
          account_number: account.account_number || '',
          is_default: true
        });
      }

      setIsEditingBank(false);
      
    } catch (err) {
      showMessage(err.message || 'Failed to save bank account', 'error');
    }
  };

  const handleDeleteBankAccount = async () => {
    if (!bankAccount.id) {
      showMessage('No bank account to delete', 'error');
      return;
    }

    if (!window.confirm('Are you sure you want to delete your bank account? You will need to add a new one to make withdrawals.')) {
      return;
    }

    try {
      await deleteBankAccount(bankAccount.id);
      showMessage('Bank account deleted successfully!', 'success');
      setBankAccount({
        id: null,
        bank_name: '',
        account_name: '',
        account_number: '',
        is_default: true
      });
      setIsEditingBank(true);
    } catch (err) {
      showMessage(err.message || 'Failed to delete bank account', 'error');
    }
  };

  const startEditingBank = () => {
    setIsEditingBank(true);
  };

  const cancelEditingBank = () => {
    if (bankAccount.id) {
      setIsEditingBank(false);
    } else {
      // If no account exists yet, keep in edit mode
      setIsEditingBank(true);
    }
  };

  // ✅ FIX: Guard against empty name fields, and use functional state
  // update (prev =>) to avoid spreading a potentially stale bankAccount object
  const autofillAccountName = () => {
    if (!surname || !firstName || !middleName) {
      showMessage('Please fill in your name fields first before autofilling', 'error');
      return;
    }
    const expectedName = `${surname.trim()} ${firstName.trim()} ${middleName.trim()}`;
    setBankAccount(prev => ({ ...prev, account_name: expectedName }));
    showMessage('Account name autofilled with your registered name', 'success');
  };

  return (
    <div className="profile-tab-content">
      <div className="tab-header">
        <span className="tab-icon">⚙️</span>
        <h2>Account Settings</h2>
      </div>

      {message && (
        <div className={`message ${messageType}`}>
          {message}
        </div>
      )}

      <div className="settings-section">
        {/* Profile Information */}
        <form onSubmit={handleProfileUpdate} className="setting-group">
          <h3 className="setting-title">👤 Profile Information</h3>
          
          <label className="setting-label">Email (Read-only)</label>
          <input
            type="email"
            className="setting-input"
            value={email}
            disabled
          />

          <label className="setting-label">Surname <span className="required">*</span></label>
          <input
            type="text"
            className="setting-input"
            placeholder="Enter surname"
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            disabled={loading}
          />
          <span className="helper-text">Letters only, no numbers or special characters</span>

          <label className="setting-label">First Name <span className="required">*</span></label>
          <input
            type="text"
            className="setting-input"
            placeholder="Enter first name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={loading}
          />
          <span className="helper-text">Letters only, no numbers or special characters</span>

          <label className="setting-label">Middle Name <span className="required">*</span></label>
          <input
            type="text"
            className="setting-input"
            placeholder="Enter middle name"
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
            disabled={loading}
          />
          <span className="helper-text">Letters only, no numbers or special characters</span>

          <label className="setting-label">Phone Number <span className="required">*</span></label>
          <input
            type="tel"
            className="setting-input"
            placeholder="Enter phone number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            disabled={loading}
          />

          <button 
            type="submit" 
            className="setting-button primary"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Profile'}
          </button>
        </form>

        {/* Change Password */}
        <form onSubmit={handlePasswordChange} className="setting-group">
          <h3 className="setting-title">🔒 Change Password</h3>

          <label className="setting-label">Current Password <span className="required">*</span></label>
          <input
            type="password"
            className="setting-input"
            placeholder="Enter current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={loading}
          />

          <label className="setting-label">New Password <span className="required">*</span></label>
          <input
            type="password"
            className="setting-input"
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading}
          />
          
          {/* Password Strength Indicator */}
          {newPassword && (
            <div className="password-strength">
              <div className={`strength-item ${passwordStrength.hasLength ? 'valid' : 'invalid'}`}>
                {passwordStrength.hasLength ? '✓' : '○'} At least 8 characters
              </div>
              <div className={`strength-item ${passwordStrength.hasLetter ? 'valid' : 'invalid'}`}>
                {passwordStrength.hasLetter ? '✓' : '○'} Contains letters
              </div>
              <div className={`strength-item ${passwordStrength.hasNumber ? 'valid' : 'invalid'}`}>
                {passwordStrength.hasNumber ? '✓' : '○'} Contains numbers
              </div>
              <div className={`strength-item ${passwordStrength.hasSpecial ? 'valid' : 'invalid'}`}>
                {passwordStrength.hasSpecial ? '✓' : '○'} Contains special characters
              </div>
            </div>
          )}

          <label className="setting-label">Confirm Password <span className="required">*</span></label>
          <input
            type="password"
            className="setting-input"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
          />
          {confirmPassword && (
            <span className={`helper-text ${newPassword === confirmPassword ? 'success' : 'error'}`}>
              {newPassword === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
            </span>
          )}

          <button 
            type="submit" 
            className="setting-button primary"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        {/* Single Bank Account */}
        <div className="setting-group">
          <div className="setting-header-with-button">
            <h3 className="setting-title">🏦 Bank Account</h3>
            {!isEditingBank && bankAccount.id && (
              <button 
                type="button"
                className="edit-bank-button"
                onClick={startEditingBank}
                disabled={loading}
              >
                ✏️ Edit
              </button>
            )}
          </div>

          <p className="bank-account-note">
            ⚠️ <strong>Important:</strong> Your bank account name must match your registered name: 
            <strong> {surname} {firstName} {middleName}</strong>
          </p>

          {isEditingBank ? (
            <form onSubmit={handleBankAccountSave} className="bank-account-form">
              <label className="setting-label">Bank Name <span className="required">*</span></label>
              <input
                type="text"
                className="setting-input"
                placeholder="Enter bank name (e.g., GTBank, Access Bank)"
                value={bankAccount.bank_name}
                onChange={(e) => setBankAccount(prev => ({ ...prev, bank_name: e.target.value }))}
                disabled={loading}
              />

              <label className="setting-label">
                Account Name <span className="required">*</span>
                <button
                  type="button"
                  className="autofill-button"
                  onClick={autofillAccountName}
                  disabled={loading}
                  style={{ marginLeft: '10px', fontSize: '12px', padding: '4px 8px' }}
                >
                  📝 Auto-fill
                </button>
              </label>
              <input
                type="text"
                className="setting-input"
                placeholder={`Must be: ${surname} ${firstName} ${middleName}`}
                value={bankAccount.account_name}
                onChange={(e) => setBankAccount(prev => ({ ...prev, account_name: e.target.value }))}
                disabled={loading}
              />
              <span className="helper-text">
                Must exactly match: <strong>{surname} {firstName} {middleName}</strong> (case-insensitive)
              </span>

              <label className="setting-label">Account Number <span className="required">*</span></label>
              <input
                type="text"
                className="setting-input"
                placeholder="Enter 10-digit account number"
                value={bankAccount.account_number}
                onChange={(e) => setBankAccount(prev => ({ ...prev, account_number: e.target.value }))}
                disabled={loading}
                maxLength="10"
              />

              <div className="bank-account-buttons">
                <button 
                  type="submit" 
                  className="setting-button primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (bankAccount.id ? 'Update Bank Account' : 'Add Bank Account')}
                </button>
                
                {bankAccount.id && (
                  <button 
                    type="button"
                    className="setting-button secondary"
                    onClick={cancelEditingBank}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                )}
                
                {bankAccount.id && (
                  <button 
                    type="button"
                    className="setting-button danger"
                    onClick={handleDeleteBankAccount}
                    disabled={loading}
                    style={{ marginLeft: '10px' }}
                  >
                    Delete Account
                  </button>
                )}
              </div>
            </form>
          ) : bankAccount.id ? (
            <div className="bank-account-display">
              <div className="bank-account-card">
                <div className="bank-account-info">
                  <div className="bank-name">{bankAccount.bank_name}</div>
                  <div className="account-details">
                    {bankAccount.account_name} • {bankAccount.account_number}
                  </div>
                  <span className="default-badge">Default</span>
                </div>
                <div className="bank-account-note">
                  This is your withdrawal account. Click "Edit" above to make changes.
                </div>
              </div>
            </div>
          ) : (
            <div className="no-bank-account">
              <p className="no-account-message">
                No bank account set up yet. Please add your bank account details to enable withdrawals.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;